import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  LinearProgress
} from '@mui/material';
import { CloudUpload, Close, AddPhotoAlternate } from '@mui/icons-material';
import { hybridUploadService } from '../../services/upload/hybridUploadService';
import WebhookService from '../../services/api/webhookService';

const ImageUploader = ({ influencer, influencerId, folder = 'raw', onUploadComplete }) => {
  // Support both influencer object and influencerId for backward compatibility
  const currentInfluencerId = influencer?.id || influencerId;
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);

  const handleOpen = () => {
    setOpen(true);
    setFiles([]);
    setPreviewUrls([]);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
  };

  const handleClose = () => {
    if (!uploading) {
      setOpen(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Filter for image files only
    const imageFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== selectedFiles.length) {
      setError('Some files were skipped because they are not images.');
    }
    
    setFiles(imageFiles);
    
    // Create preview URLs
    const urls = imageFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
    
    setError(null);
    setSuccess(false);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one image to upload.');
      return;
    }
    
    setUploading(true);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    
    try {
      // Send webhook notification when upload starts (only once, not for every image)
      if (influencer && files.length > 0) {
        await WebhookService.sendImageUploadNotification(influencer, files.length);
      }
      
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      const errors = [];
      
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        
        try {
          // Use hybrid upload service - automatically chooses method based on file size
          const result = await hybridUploadService.uploadFile(
            file,
            currentInfluencerId,
            folder,
            (progressPercent) => {
              // Update individual file progress within overall progress
              const overallProgress = Math.round(((i + progressPercent / 100) / totalFiles) * 100);
              setUploadProgress(overallProgress);
            },
            null, // onSuccess callback - we handle success in the main flow
            null  // onError callback - we handle errors in the catch block
          );
          
          if (result.success) {
            successCount++;
            console.log(`Successfully uploaded ${file.name} using ${result.method} method`);
          } else {
            failCount++;
            errors.push(`${file.name}: ${result.error}`);
          }
          
        } catch (err) {
          console.error(`Error uploading file ${file.name}:`, err);
          failCount++;
          errors.push(`${file.name}: ${err.message || 'Unknown error'}`);
        }
        
        // Update progress for completed file
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }
      
      // Show results
      if (failCount > 0) {
        const errorMessage = `${failCount} out of ${totalFiles} files failed to upload.`;
        const detailedError = errors.length > 0 ?
          `${errorMessage}\n\nDetails:\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}` :
          errorMessage;
        setError(detailedError);
      }
      
      if (successCount > 0) {
        setSuccess(true);
        
        // Clean up preview URLs
        previewUrls.forEach(url => URL.revokeObjectURL(url));
        
        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete(successCount);
        }
        
        // Close dialog after a delay if all uploads succeeded
        if (failCount === 0) {
          setTimeout(() => {
            setOpen(false);
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Error during upload:', err);
      setError('An error occurred during upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    
    const newPreviewUrls = [...previewUrls];
    URL.revokeObjectURL(newPreviewUrls[index]);
    newPreviewUrls.splice(index, 1);
    setPreviewUrls(newPreviewUrls);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddPhotoAlternate />}
        onClick={handleOpen}
        sx={{
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
      >
        Add Multiple Images
      </Button>
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddPhotoAlternate color="primary" />
          Upload Multiple Images
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select multiple images to upload to the {folder} folder for this influencer.
            You can select and upload multiple images at once to save time.
          </DialogContentText>
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-images"
              multiple
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor="upload-images">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                disabled={uploading}
                size="large"
                sx={{
                  minHeight: 48,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                Select Multiple Images
              </Button>
            </label>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {files.length > 0 ? `${files.length} image${files.length > 1 ? 's' : ''} selected for upload` : 'No images selected - You can select multiple images at once'}
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Images uploaded successfully!
            </Alert>
          )}
          
          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
          
          {previewUrls.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Preview:
              </Typography>
              <Grid container spacing={1}>
                {previewUrls.map((url, index) => (
                  <Grid item xs={3} key={index}>
                    <Box
                      sx={{
                        position: 'relative',
                        height: 100,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)'
                          }
                        }}
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={files.length === 0 || uploading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageUploader;
